// Settings (design §14.5). The privacy statements are §16 verbatim, because
// they describe what actually leaves the device and must not drift into
// reassurance; the usage figures are presented factually and quietly, because
// cost here is information and not a warning (design §12.3).

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  type AvailableModel,
  type Me,
  type Tier,
  type Usage,
  type UsageRow,
  api,
} from "../api/client";
import { OUTBOX, count } from "../storage/db";
import { forgetDevice, rememberMe } from "../storage/me";
import { BackDisc, TopBar } from "../ui/TopBar";

const TIER_LABELS: Record<Tier, string> = {
  review: "Reviewing, whether you asked or it did",
  small: "Evaluating and researching",
};

const KIND_LABELS: Record<string, string> = {
  review: "Reviews",
  evaluation: "Where you stand",
  research: "Research",
  ask: "Questions about a passage",
  background: "Quiet reading",  // runs recorded before Phase 4.5
  reassess: "Reassessments",
  progress: "Goal cards",
};

function money(amount: number): string {
  // Three places, because a small call costs a third of a cent and rounding it
  // to two would show the cheapest tier in the product as free.
  return `$${amount.toFixed(3)}`;
}

function dollars(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

const PROVIDER_NAMES: Record<string, string> = {
  anthropic: "Anthropic",
  google: "Google",
  mistral: "Mistral",
};

/** The model a tier actually runs on: the override if there is one, else the
    one this deployment is set to. */
function inUse(me: Me, tier: Tier): string {
  return me.model_overrides[tier] ?? me.model_defaults[tier];
}

function modelsFor(me: Me, tier: Tier): AvailableModel[] {
  return me.available_models.filter((m) => m.tiers.includes(tier));
}

/** Who the author's prose is actually sent to. Read from the setting rather
    than written into the copy, so §16 cannot drift from what the server does. */
function providersInUse(me: Me): string {
  const names = [...new Set((Object.keys(TIER_LABELS) as Tier[]).map((tier) => {
    const found = me.available_models.find((m) => m.id === inUse(me, tier));
    return found ? (PROVIDER_NAMES[found.provider] ?? found.provider) : null;
  }).filter((n): n is string => n !== null))];
  if (names.length === 0) return "the model provider's API";
  if (names.length === 1) return `${names[0]}'s API`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}'s APIs`;
}

export function Settings() {
  const navigate = useNavigate();
  const [me, setMe] = useState<Me | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [budget, setBudget] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .get<Me>("/api/me")
      .then((value) => {
        if (cancelled) return;
        setMe(value);
        setBudget(value.monthly_budget_usd === null ? "" : String(value.monthly_budget_usd));
      })
      .catch(() => !cancelled && setError("Could not load your settings."));
    // Separately, and without an error of its own: the figures are worth
    // having but no setting depends on them, and a screen that will not open
    // because a total would not load is a worse screen.
    api
      .get<Usage>("/api/me/usage")
      .then((value) => !cancelled && setUsage(value))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (patch: Record<string, unknown>) => {
    setError(null);
    try {
      const updated = await api.patch<Me>("/api/me", patch);
      setMe(updated);
      rememberMe(updated);
    } catch {
      setError("Could not save that. Check your connection.");
    }
  };

  const saveBudget = () => {
    const trimmed = budget.trim();
    if (trimmed === "") return void save({ clear_budget: true });
    const value = Number(trimmed);
    if (!Number.isFinite(value) || value < 0) {
      setError("The budget must be a number, or empty for no ceiling.");
      return;
    }
    void save({ monthly_budget_usd: value });
  };

  const exportAll = async () => {
    try {
      const bundle = await api.get<unknown>("/api/me/export");
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `metis-export-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Could not export. Check your connection.");
    }
  };

  const signOut = async () => {
    // Signing out takes this device's copy with it, so a shared iPad does not
    // open on someone else's library. Edits that have not reached the server
    // yet would go with it, which deserves a question first.
    if (
      (await count(OUTBOX)) > 0 &&
      !window.confirm(
        "Some edits on this device have not reached the server yet. Sign out and lose them?",
      )
    ) {
      return;
    }
    try {
      await api.post("/api/auth/logout");
    } catch {
      // signing out locally still matters
    }
    await forgetDevice();
    navigate("/signin", { replace: true });
  };

  const deleteAccount = async () => {
    if (!me) return;
    const sure = window.confirm(
      `Delete the account ${me.email} and every document in it? This cannot be undone. ` +
        "Export everything first if you want a copy.",
    );
    if (!sure) return;
    try {
      await api.delete("/api/me");
    } catch {
      setError("Could not delete the account. Check your connection.");
      return;
    }
    await forgetDevice();
    navigate("/signin", { replace: true });
  };

  return (
    <div className="screen">
      <TopBar title="Settings" settings={false} />

      <main className="body settings">
        <BackDisc />
        {error && <p className="error">{error}</p>}
        {!me && !error && <p className="muted">Loading…</p>}

        {me && (
          <>
            <section>
              <h2>Account</h2>
              <p className="muted">{me.email}</p>
              <button type="button" className="text-button flush" onClick={signOut}>
                Sign out
              </button>
            </section>

            <section>
              <h2>The advisor</h2>
              <label className="row-setting">
                <input
                  type="checkbox"
                  checked={me.default_search_enabled}
                  onChange={(e) => void save({ default_search_enabled: e.target.checked })}
                />
                <span>
                  Let new documents search the web
                  <small>
                    Off by default. With search on, fragments of an unfinished document become
                    queries. You can turn it on per document as well.
                  </small>
                </span>
              </label>

              <label className="row-setting">
                <input
                  type="checkbox"
                  checked={me.background_enabled}
                  onChange={(e) => void save({ background_enabled: e.target.checked })}
                />
                <span>
                  Keep reading while I write
                  <small>
                    Sends a paragraph and its neighbours for a quick look shortly after you stop
                    editing it. Never searches the web.
                  </small>
                </span>
              </label>

              <label className="row-setting">
                <span className="budget-label">Monthly ceiling</span>
                <span>
                  <span className="budget-input">
                    <span aria-hidden>$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={budget}
                      placeholder="none"
                      onChange={(e) => setBudget(e.target.value)}
                      onBlur={saveBudget}
                      aria-label="Monthly budget in dollars"
                    />
                  </span>
                  <small>
                    Crossing it suspends the reading it does unasked and says so. The button keeps
                    working; it never stops mid-sentence.
                  </small>
                </span>
              </label>

              {!me.limits.unlimited && (
                <p className="muted small">
                  This server allows each account {dollars(me.limits.daily_usd)} a day and{" "}
                  {dollars(me.limits.monthly_usd)} a month, including the reviews you ask for. Today{" "}
                  {money(me.limits.spent_today_usd)}, this month{" "}
                  {money(me.limits.spent_this_month_usd)}.
                </p>
              )}

              {me.budget.over && (
                <p className="over-budget" role="status">
                  You have spent {money(me.budget.spent_this_month_usd)} this month, past the
                  ceiling of {money(me.budget.ceiling_usd ?? 0)}. Reading in the background is
                  suspended until next month or until you raise the ceiling. The Review button
                  still works.
                </p>
              )}
            </section>

            {/* Not behind a disclosure: which model is reading your writing is
                not an advanced detail, it is the single fact that explains
                what the advisor says and what it costs. */}
            <section>
              <h2>Which model does the work</h2>
              <p className="muted small">
                Normally you choose the scope and the application chooses the model. Change these
                only if you have a reason to.
              </p>
              {(Object.keys(TIER_LABELS) as Tier[]).map((tier) => (
                <label className="row-setting" key={tier}>
                  <span className="budget-label">{TIER_LABELS[tier]}</span>
                  <span>
                    <select
                      value={me.model_overrides[tier] ?? ""}
                      aria-label={TIER_LABELS[tier]}
                      onChange={(e) =>
                        void save({
                          model_overrides: {
                            ...me.model_overrides,
                            [tier]: e.target.value || null,
                          },
                        })
                      }
                    >
                      <option value="">
                        This server's choice ({me.model_defaults[tier]})
                      </option>
                      {modelsFor(me, tier).map((model) => (
                        <option key={model.id} value={model.id} disabled={!model.configured}>
                          {model.label}
                          {model.configured ? "" : " — no API key on this server"}
                        </option>
                      ))}
                    </select>
                    {!me.available_models.find((m) => m.id === inUse(me, tier))?.searches && (
                      <small>
                        This model cannot search the web in a way that lets it show you the page
                        it read, so searching is off while it is chosen.
                      </small>
                    )}
                  </span>
                </label>
              ))}
            </section>

            <section>
              <h2>Usage</h2>
              {!usage || usage.lifetime_cost_usd === 0 ? (
                <p className="muted small">
                  Token and cost totals appear here once the advisor is doing work.
                </p>
              ) : (
                <>
                  <p className="muted small">
                    {money(usage.month_cost_usd)} this month, {money(usage.lifetime_cost_usd)} in
                    all.
                  </p>
                  <UsageTable caption="By model" rows={usage.by_model} />
                  <UsageTable
                    caption="By kind of call"
                    rows={usage.by_kind.map((r) => ({
                      ...r,
                      name: KIND_LABELS[r.name] ?? r.name,
                    }))}
                  />
                  <UsageTable caption="By document" rows={usage.by_document} />
                </>
              )}
            </section>

            <section>
              <h2>Data and privacy</h2>
              <ul className="plain">
                <li>
                  The document is sent to {providersInUse(me)} when a review or an evaluation
                  runs — whether you asked for it, or because enough of the text has changed.
                </li>
                <li>
                  With web search enabled, fragments of the document become queries sent to a
                  search provider.
                </li>
                <li>
                  The background pass can be turned off entirely above; web search is off by
                  default per document.
                </li>
              </ul>
            </section>

            <section>
              <h2>Export</h2>
              <p className="muted small">
                Every document as markdown. Your prose and nothing else: no notes, no comments, no
                annotation syntax.
              </p>
              <button type="button" className="text-button flush" onClick={exportAll}>
                Export everything
              </button>
            </section>

            <section>
              <h2>Delete account</h2>
              <p className="muted small">
                Removes the account and every document, version, note and comment in it from the
                server, and this device's copy with them. There is no undo.
              </p>
              <button type="button" className="text-button flush" onClick={deleteAccount}>
                Delete my account
              </button>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

/** One cut of the usage figures. Small, grey, and always there: this is
 *  information, not a warning (design §12.3). */
function UsageTable({ caption, rows }: { caption: string; rows: UsageRow[] }) {
  if (rows.length === 0) return null;
  return (
    <table className="usage">
      <caption className="muted small">{caption}</caption>
      <tbody>
        {rows.map((row) => (
          <tr key={row.name}>
            <td>{row.name}</td>
            <td className="muted">
              {row.runs} run{row.runs === 1 ? "" : "s"}
            </td>
            <td className="num muted">{money(row.month_cost_usd)} this month</td>
            <td className="num">{money(row.cost_usd)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
